// @ts-nocheck

import { useEffect, useCallback } from 'react';

import { pick, omit } from 'lodash';

import type { Game } from '@shared/types';

import useAxios from '@services/api';

import { GameBuild } from './client.types';
import type { SubmittedForm, CropPixelsData } from './client.types';
import { cropImage, rotateImages, resizeImage } from './client.utils';

const fixedPixels = { x: 100, y: 100, width: 200, height: 200 };

export const useFetchGameDefaults = (name: string) => {
  const { response, error, issueRequest } = useAxios<Game>();

  useEffect(() => {
    void issueRequest({ url: `/games/${name}`, method: 'get' });
  }, [name, issueRequest]);

  return response;
};

export const useFetchGameDefaultsLazy = () => {
  const { response, error, issueRequest, loading } = useAxios<Game>();

  const useFetchGameDefaultsLazy = useCallback((name: string) => {
    void issueRequest({ url: `/games/${name}`, method: 'get' });
  }, [issueRequest]);

  return [response, useFetchGameDefaultsLazy, loading] as const;
};

export const useFetchGameBuild = () => {
  const { response, error, issueRequest } = useAxios();

  const fetchGameBuild = useCallback((gameId: string) => {
    // console.log('Fetching game build with ID:', gameId);
    if (!gameId) {
      // console.error('Game ID is empty or undefined');
      return;
    }
    // console.log('Making request to:', `/public/games/build-data/${gameId}`);
    void issueRequest({ url: `/public/games/build-data/${gameId}`, method: 'get' });
  }, [issueRequest]);
  return [response, fetchGameBuild, error] as const;
};

// export const useFetchGameBuild = (id: string) => {
//   const { response, error, issueRequest } = useAxios<GameBuild>();

//   useEffect(() => {
//     if (!id) return;  // Проверка на пустой ID

//     void issueRequest({ 
//       url: `/game/build/${id}`, 
//       method: 'get', 
//       headers: {
//         'x-api-key': '38f8ef5e-8c83-416d-9b6d-bd9980c44429',
//       },
//     });
//   }, [id]);

//   return [response, error] as const;
// };


export const useCreateGameBuild = () => {
  const { response, error, issueRequest, loading, setLoading } =
    useAxios<GameBuild>();

  const handleSubmit = useCallback(
    (
      data: SubmittedForm,
      croppedPixelsData: CropPixelsData,
      tiles: string[]
    ) => {
      console.log('handleSubmit called with:', { data, croppedPixelsData, tiles });
      setLoading(true);

      // Предварительная блокирующая проверка: все изображения обязательны
      const missingReasons: string[] = [];
      const isEmpty = (v: any) => !v || (typeof v === 'string' && v.trim() === '');
      if (isEmpty((data as any).startGameImage) && isEmpty((data as any).startGameImageUrl)) {
        missingReasons.push('startGameImage');
      }
      if (isEmpty((data as any).endGameImage) && isEmpty((data as any).endGameImageUrl)) {
        missingReasons.push('endGameImage');
      }
      const tilesArr = Array.isArray(data.gameTiles) ? data.gameTiles : [];
      const emptyTileIdx: number[] = [];
      for (let i = 0; i < 12; i++) {
        const v = tilesArr[i];
        if (isEmpty(v)) {
          emptyTileIdx.push(i + 1);
        }
      }
      if (emptyTileIdx.length > 0) {
        missingReasons.push(`tiles:[${emptyTileIdx.join(',')}]`);
      }
      if (missingReasons.length > 0) {
        console.error('Blocking submit due to missing images:', missingReasons);
        setLoading(false);
        return Promise.reject(new Error('Пожалуйста, заполните все изображения (12 карточек + старт/финал).'));
      }
      console.log('Начало загрузки файлов:', { 
        startGameImage: data.startGameImage,
        endGameImage: data.endGameImage,
        gameTiles: data.gameTiles,
        customMusicFile: data.customMusicFile
      });
      console.log('Cropped pixels data:', croppedPixelsData);
      console.log('Cropped pixels data keys:', Object.keys(croppedPixelsData));

      const pendingPromises = [];

      for (const [fieldName, pixels] of Object.entries(croppedPixelsData)) {
        if (!pixels) {
          continue;
        }
        console.log(`Processing crop data for field: ${fieldName}`, pixels);
        
        // Проверяем, является ли это игровой плиткой (gameTiles.0, gameTiles.1, и т.д.)
        if (fieldName.startsWith('gameTiles.')) {
          const tileIndex = parseInt(fieldName.split('.')[1], 10);
          const tileFile = data.gameTiles[tileIndex];
          console.log(`Processing tile ${tileIndex}:`, tileFile);
          if (tileFile && (tileFile instanceof File || typeof tileFile === 'string')) {
            pendingPromises.push(
              cropImage(tileFile, pixels).then(
                (croppedImage) => {
                  console.log(`Cropped tile ${tileIndex}:`, croppedImage);
                  data.gameTiles[tileIndex] = croppedImage;
                }
              ).catch((error) => {
                console.error(`Error cropping tile ${tileIndex}:`, error);
              })
            );
          } else {
            console.log(`No file for tile ${tileIndex}`);
          }
        } else if (!isNaN(parseInt(fieldName, 10))) {
          // Обратная совместимость для старых ключей
          const tileIndex = parseInt(fieldName, 10);
          const tileFile = data.gameTiles[tileIndex];
          console.log(`Processing tile ${tileIndex}:`, tileFile);
          if (tileFile && (tileFile instanceof File || typeof tileFile === 'string')) {
            pendingPromises.push(
              cropImage(tileFile, pixels).then(
                (croppedImage) => {
                  console.log(`Cropped tile ${tileIndex}:`, croppedImage);
                  data.gameTiles[tileIndex] = croppedImage;
                }
              ).catch((error) => {
                console.error(`Error cropping tile ${tileIndex}:`, error);
              })
            );
          } else {
            console.log(`No file for tile ${tileIndex}`);
          }
        } else {
          const fieldValue = data[fieldName];
          console.log(`Processing field ${fieldName}:`, fieldValue);
          if (fieldValue && (fieldValue instanceof File || typeof fieldValue === 'string')) {
            pendingPromises.push(
              cropImage(fieldValue, pixels).then((croppedImage) => {
                console.log(`Cropped field ${fieldName}:`, croppedImage);
                data[fieldName] = croppedImage;
              }).catch((error) => {
                console.error(`Error cropping field ${fieldName}:`, error);
              })
            );
          } else {
            console.log(`No file for field ${fieldName}`);
          }
        }
      }

      Promise.all(pendingPromises)
        .then(() => {
          console.log('Все изображения обрезаны, начинаем подготовку к отправке');
          console.log('Final gameTiles data:', data.gameTiles);
          console.log('Final data object:', data);
          console.log('Tiles parameter:', tiles);
          
          // НЕ ПЕРЕЗАПИСЫВАЕМ gameTiles - используем только те, что уже есть в data
          // rotateImages больше не применяется, так как это может перезаписать обрезанные файлы
          console.log('Using existing gameTiles without rotation:', data.gameTiles);
          
          // Убеждаемся, что gameTiles существует и является массивом
          if (!data.gameTiles || !Array.isArray(data.gameTiles)) {
            console.error('gameTiles is missing or not an array:', data.gameTiles);
            data.gameTiles = Array(12).fill('');
          }

          const formData = new FormData();

          const fileFields = [
            'startGameImage',
            'endGameImage',
            'gameTiles',
            'customMusicFile',
            'urlButton',
          ];
          const fileUrls = <(string | null)[]>[];

          // Убираем проверку enableCustomMusic - музыкальный файл должен загружаться всегда
          // if (!data.enableCustomMusic) {
          //   delete data.customMusicFile;
          // }

          console.log('Data before pick:', data);
          console.log('Data.customMusicFile:', data.customMusicFile);
          console.log('Data.gameTiles:', data.gameTiles);
          const files: Pick<
            SubmittedForm,
            'startGameImage' | 'endGameImage' | 'gameTiles' | 'customMusicFile' | 'urlButton'
          > = pick(data, fileFields);
          console.log('Files after pick:', files);
          console.log('Files.gameTiles:', files.gameTiles);
          const plainFields: Omit<
            SubmittedForm,
            'startGameImage' | 'endGameImage' | 'customMusicFile' | 'gameTiles' | 'fileDownload'
          > & { gameTiles: (string | null)[] } = omit(data, fileFields);
          
          console.log('Data before omit:', data);
          console.log('File fields:', fileFields);
          console.log('Plain fields after omit:', plainFields);
          console.log('About to start file processing...');
          
          console.log('Data before omit:', data);
          console.log('File fields:', fileFields);

          console.log('Подготовка файлов для отправки:', { 
            files: Object.keys(files),
            plainFields: Object.keys(plainFields),
            data: data
          });
          console.log('Files object:', files);
          console.log('Files customMusicFile:', files.customMusicFile);

          for (const [fieldName, fileObject] of Object.entries(files)) {
            console.log(`Processing field: ${fieldName}`, fileObject);
            if (fieldName === 'gameTiles') {
              console.log('Processing gameTiles:', files[fieldName]);
              console.log('Files[fieldName] type:', typeof files[fieldName]);
              console.log('Files[fieldName] is array:', Array.isArray(files[fieldName]));
              
              if (!files[fieldName] || !Array.isArray(files[fieldName])) {
                console.error('gameTiles is missing or not an array in files object');
                continue;
              }
              
              for (let i = 0; i < files[fieldName].length; i++) {
                const imageFile = files[fieldName][i];
                console.log(`GameTile ${i}:`, imageFile);
                if (typeof imageFile === 'string' && imageFile && imageFile !== '') {
                  fileUrls.push(imageFile);
                  console.log(`Added string URL for tile ${i}:`, imageFile);
                } else if (imageFile instanceof File) {
                  fileUrls.push(null);
                  formData.append(`gameTiles[${i}]`, imageFile);
                  // Добавляем информацию о том, что файл уже обрезан
                  formData.append(`gameTiles[${i}].cropped`, 'true');
                  console.log(`Added cropped file for tile ${i}:`, imageFile);
                } else {
                  fileUrls.push(null);
                  console.log(`No file for tile ${i}`);
                }
              }
            } else if (fileObject instanceof File) {
              // Специальная обработка для urlButton: если File - отправляем как fileDownload
              if (fieldName === 'urlButton') {
                formData.append('fileDownload', fileObject);
                plainFields.urlButton = ''; // Очищаем urlButton, так как используется файл
                console.log(`Added urlButton file as fileDownload:`, fileObject.name, 'size:', fileObject.size);
              } else {
                formData.append(fieldName, fileObject);
                // Добавляем информацию о том, что файл уже обрезан
                formData.append(`${fieldName}.cropped`, 'true');
                console.log(`Added cropped file for ${fieldName}:`, fileObject);
              }
              if (fieldName === 'customMusicFile') {
                console.log(`Added music file:`, fileObject.name, 'size:', fileObject.size);
              }
            } else if (typeof fileObject === 'string') {
              if (fieldName === 'customMusicFile') {
                plainFields.musicUrl = fileObject;
                console.log(`Set musicUrl:`, fileObject);
              } else if (fieldName === 'urlButton') {
                plainFields.urlButton = fileObject;
                plainFields.fileDownloadUrl = ''; // Очищаем fileDownloadUrl, так как используется URL
                console.log(`Set urlButton:`, fileObject);
              } else if (fieldName === 'startGameImage') {
                plainFields.startGameImageUrl = fileObject;
                console.log(`Set startGameImageUrl:`, fileObject);
              } else if (fieldName === 'endGameImage') {
                plainFields.endGameImageUrl = fileObject;
                console.log(`Set endGameImageUrl:`, fileObject);
              } else {
                fileUrls.push(fileObject);
                console.log(`Added string URL for ${fieldName}:`, fileObject);
              }
            }
          }

          plainFields.gameTiles = fileUrls;
          
          // Добавляем информацию о том, что файлы уже обрезаны
          const croppedFilesList = [];
          for (const [fieldName, fileObject] of Object.entries(files)) {
            if (fieldName === 'gameTiles') {
              for (let i = 0; i < files[fieldName].length; i++) {
                const imageFile = files[fieldName][i];
                if (imageFile instanceof File) {
                  croppedFilesList.push(`gameTiles.${i}`);
                }
              }
            } else if (fileObject instanceof File) {
              croppedFilesList.push(fieldName);
            }
          }
          plainFields.croppedFiles = croppedFilesList;

          console.log('Cropped files list:', croppedFilesList);
          console.log('Files being processed:', files);

          const jsonData = JSON.stringify(plainFields);

          formData.append('jsonData', jsonData);

          console.log('JSON data being sent:', jsonData);
          console.log('Plain fields:', plainFields);
          console.log('File URLs:', fileUrls);
          console.log('Plain fields.gameTiles:', plainFields.gameTiles);
          console.log('Отправка данных на сервер...');
          issueRequest({
            url: `/games/build/memory`,
            method: 'put',
            body: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then((response) => {
            console.log('Server response after creation:', response);
          }).catch((error) => {
            console.error('Error creating game build:', error);
          });
        })
        .finally(() => {
          console.log('Загрузка завершена');
          setLoading(false);
        });
    },
    []
  );

  return [response, handleSubmit, loading] as const;
};

export const useSetActivateGameDate = () => {
  const { response, error, issueRequest } = useAxios<Record<string, never>>();

  const useSetActivateGameDate = useCallback((id: string) => {
    void issueRequest({ url: `/games/keys/${id}`, method: 'put' });
  }, []);

  return [response, useSetActivateGameDate] as const;
};
